import { useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useImportPermit } from '../hooks/useImportPermit';

interface Props {
  open: boolean;
  onClose: () => void;
  disabled?: boolean;
}

export default function PermitImportDialog({ open, onClose, disabled = false }: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importPermit = useImportPermit();

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    importPermit.reset();
  }

  async function handleSubmit() {
    if (!selectedFile) return;
    try {
      await importPermit.mutateAsync(selectedFile);
      handleClose();
    } catch {
      // Error is captured in importPermit.error
    }
  }

  function handleClose() {
    setSelectedFile(null);
    importPermit.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="permit-import-dialog"
    >
      <DialogTitle>Import Permit</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a permit file (.txt or .zip) to import holdings.
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.zip"
          onChange={handleFileChange}
          disabled={disabled || importPermit.isPending}
        />
        {importPermit.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {importPermit.error?.message ?? 'Import failed'}
          </Alert>
        )}
        {importPermit.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Permit imported successfully
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importPermit.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!selectedFile || disabled || importPermit.isPending}
          startIcon={importPermit.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {importPermit.isPending ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
